import {TranslateService} from '@ngx-translate/core';
import {Injectable, signal} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, switchMap, take, tap} from 'rxjs';
import {SavedView} from '../interfaces/saved-view';
import {ViewResource} from '../resource/view.resource';
import {CytoscapeService} from '../../services/cy-services/cytoscape.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../../dialogs/confirmation-dialog.component';
import {TourResource} from '../resource/tour.resource';
import {LinkViewMode, SettingsService} from './setting.service';
import {OtherNodesMode, SidebarState} from '@maporium-workspace/shared';
import {clearOtherClasses} from '../../dashboard/other-elements-display';
import {TourStep} from '../interfaces/map-tour';
import {MapService} from './map.service';
import {PackageService} from './package.service';
import {NodeService} from './node.service';
import {MapStateService} from './map-state.service';
import {invalidatePanZoomForCollapsedNode, savePanZoomState} from '../../../cytoscape-extensions/pan-zoom-storage';
import {isInIframe} from '../../shared/helpers/maporium.validators';
import {DashboardComponent} from '../../dashboard/dashboard.component';
import {MapRoleService} from '../../acl/map-role.service';
import {AudioService} from '../../services/audio.service';
import {TTSService} from '../../services/tts.service';

@Injectable({providedIn: 'root'})
export class ViewService {
  public viewChangeInProgress = false;
  public viewHasLoaded = new Subject<boolean>();
  /**
   * MAP-389: Emits the scene that is currently being viewed standalone (i.e.
   * not as part of a story). Drives the bottom scene panel. null when no
   * standalone scene is active or when the panel was explicitly dismissed.
   */
  public activeScene$ = new BehaviorSubject<SavedView | null>(null);
  private currentViewsSignal = signal<SavedView[]>([]);
  private currentPublicViewsSignal = signal<SavedView[]>([]);

  constructor(private translate: TranslateService,
              private resource: ViewResource,
              private mapService: MapService,
              private mapStateService: MapStateService,
              private tourResource: TourResource,
              private packageService: PackageService,
              private nodeService: NodeService,
              private settingsService: SettingsService,
              private matDialog: MatDialog,
              private cyService: CytoscapeService,
              private mapRoleService: MapRoleService,
              private audioService: AudioService,
              private ttsService: TTSService) {
  }

  get currentViews(): SavedView[] {
    return this.currentViewsSignal();
  }

  get currentPublicViews(): SavedView[] {
    return this.currentPublicViewsSignal();
  }

  public getAll(mapId: string): Observable<SavedView[]> {
    return this.resource.getAll(mapId)
      .pipe(
        tap((views) => {
          this.currentViewsSignal.set(views);
        }));
  }

  public getAllPublic(mapId: string): Observable<SavedView[]> {
    return this.resource.getAllPublic(mapId)
      .pipe(
        tap((views) => {
          this.currentPublicViewsSignal.set(views);
        }));
  }

  public create(view: SavedView): Observable<SavedView> {
    return this.resource.create(view);
  }

  public update(view: SavedView): Observable<SavedView> {
    return this.resource.update(view).pipe(
      tap(updated => {
        this.currentViewsSignal.set(this.currentViewsSignal().map(v => v.id === updated.id ? updated : v));
        this.currentPublicViewsSignal.set(this.currentPublicViewsSignal().map(v => v.id === updated.id ? updated : v));
        const active = this.activeScene$.value;
        if (active && active.id === updated.id) {
          this.activeScene$.next(updated);
        }
      })
    );
  }

  public delete(view: SavedView, success?: () => void): void {
    this.tourResource.getAll(view.map.id)
      .pipe(
        take(1), // Ensure single emission and auto-unsubscribe
        switchMap((tours) => {
          const viewIsInTour = tours.some((tour) => {
            return tour.steps.some((step) => {
              return step.savedView.id === view.id;
            });
          });
          const toursWithView = tours.filter((tour) => {
            return tour.steps.some((step) => {
              return step.savedView.id === view.id;
            });
          });
          const tourNames = toursWithView.map((tour) => {
            return tour.name;
          });

          if (viewIsInTour) {
            const ref = this.matDialog.open(ConfirmationDialogComponent, {
              width: '540px',
              maxWidth: '90vw',
              data: {
                title: 'Confirm Deletion',
                message: `This scene is used in the following stories and will be removed from them:
                '${tourNames.join('\', \'')}' \n \n Are you still sure that you want to delete the '${view.name}' scene?`
              }
            });
            return ref.afterClosed().pipe(
              take(1),
              switchMap((result) => {
                if (result) {
                  return this.resource.delete(view);
                }
                return of(null);
              })
            );
          } else {
            return this.resource.delete(view);
          }
        })
      )
      .subscribe((result) => {
        if (result) {
          if (this.activeScene$.value?.id === view.id) {
            this.activeScene$.next(null);
          }
          this.currentViewsSignal.set(this.currentViewsSignal().filter(v => v.id !== view.id));
          this.currentPublicViewsSignal.set(this.currentPublicViewsSignal().filter(v => v.id !== view.id));
          if (success) {
            success();
          }
        }
      });
  }

  public async getCtxMenuItems() {
    const mapId = this.mapService.getCurrentSelectedMapFromStore().id;
    if (!mapId) {
      return null;
    }

    const views = isInIframe() ? await this.getAllPublic(mapId).toPromise() : await this.getAll(mapId).toPromise();

    if (!views || views.length === 0) {
      return null;
    }

    const subMenuWithViews = views.map((view) => {
      return {
        id: view.id,
        content: view.name,
        tooltipText: view.description,
        onClickFunction: (event: any) => {
          this.playView(view);
        },
      };
    });
    return {
      id: 'views_menu',
      content: this.translate.instant('CONTEXT_MENU.VIEW_SCENE'),
      image: {src: 'assets/mat-symbols/outlined/location_on.svg', width: 12, height: 12, x: 5, y: 6},
      coreAsWell: true,
      submenu: subMenuWithViews
    };
  }

  public async playView(view: SavedView, step?: TourStep, end?: () => void) {
    // Stop any current speech before playing new view
    this.ttsService.stop();

    // MAP-389: Surface the standalone scene to the scene panel; story playback
    // owns its own panel and intentionally suppresses this one — clearing the
    // standalone scene when a story step plays prevents stale content from
    // re-appearing when the story ends. The showDescription flag controls only
    // the description body inside the panel; the title bar always appears.
    if (step) {
      if (this.activeScene$.value) this.activeScene$.next(null);
    } else {
      this.activeScene$.next(view);
    }

    invalidatePanZoomForCollapsedNode();
    const cy = this.cyService.cy;
    if (!cy) {
      return;
    }

    // Show debug overlay if enabled via localStorage
    this.showExtentOverlay(view.extent);

    this.viewChangeInProgress = true;
    // @ts-ignore
    const expandCollapse = cy.expandCollapse('get');
    const collapsedNodes = view.collapsedNodes;
    const selectedElementIds = structuredClone(view.selected);
    const currentSelectedState = this.mapStateService.getCurrentLocalState();
    const timeout = 350;

    /**
     * MAP-1608: restoring the selection and the link display mode restyles every
     * edge, which on a smap with labelled links is a long synchronous task. It
     * used to run on a fixed 200ms timer, landing squarely inside the camera
     * animation and stalling it once, visibly. Wait for the camera to arrive
     * instead: nothing here is visible during the flight anyway.
     */
    let cameraSettled: Promise<void> = Promise.resolve();

    const toggleSidebar = () => {
      if (view.sidebarState !== null && view.sidebarState === SidebarState.FORCE_OPEN) {
        this.settingsService.storeSidebarWidth(view.metadata?.sidebarWidth || 300);
        this.settingsService.toggleSidebar({forceOpen: true});
      } else if (view.sidebarState !== null && view.sidebarState === SidebarState.FORCE_CLOSE) {
        this.settingsService.toggleSidebar({forceClose: true});
      }
    };
    toggleSidebar();

    const moveViewIntoScene = async (shouldAnimate = true, end?: () => void) => {
      const cy = this.cyService.cy;
      if (!cy || !view.extent) return;

      const bbox = view.extent;
      // Compute saved and current visible canvas percentages
      const savedSidebarWidth = view.sidebarState === SidebarState.FORCE_CLOSE ? 0 : view.metadata?.sidebarWidth ?? 0;
      const savedContainerWidth = view.metadata?.containerWidth ?? cy.width();
      const isSidebarOpen = this.settingsService.isSidebarOpen();
      const currentSidebarWidth = isSidebarOpen ? this.settingsService.getSidebarWidth() : 0;
      const currentContainerWidth = cy.width();


      const P = 1 - (savedSidebarWidth / savedContainerWidth);
      const PP = 1 - (currentSidebarWidth / currentContainerWidth);

      // Scale the saved extent width by P to get CR
      const originalExtentWidth = bbox.x2 - bbox.x1;
      const scaledExtentWidth = originalExtentWidth * P;

      const scaledExtent = {
        x1: bbox.x1,
        y1: bbox.y1,
        x2: bbox.x1 + scaledExtentWidth,
        y2: bbox.y2,
      };

      const targetVisibleWidth = currentContainerWidth * PP;
      const targetSize = {
        width: targetVisibleWidth,
        height: cy.height()
      };

      const panZoom = this.getPanZoomFromExtent(scaledExtent, targetSize);

      if (panZoom && view.sidebarState === SidebarState.FORCE_OPEN) {
        // Apply padding logic
        const sidebarWidth = currentSidebarWidth;
        const bboxWidth = scaledExtent.x2 - scaledExtent.x1;
        const bboxHeight = scaledExtent.y2 - scaledExtent.y1;
        const containerWidth = cy.width();
        const containerHeight = cy.height();
        const effWidth = containerWidth - sidebarWidth;
        const padding = 1;
        const availableWidth = effWidth - 2 * padding;
        const availableHeight = containerHeight - 2 * padding;
        const widthZoom = availableWidth / bboxWidth;
        const heightZoom = availableHeight / bboxHeight;
        const zoom = Math.min(widthZoom, heightZoom);

        const centerX = (scaledExtent.x1 + scaledExtent.x2) / 2;
        const centerY = (scaledExtent.y1 + scaledExtent.y2) / 2;

        panZoom.zoom = zoom;
        panZoom.pan = {
          x: padding + availableWidth / 2 - centerX * zoom,
          y: padding + availableHeight / 2 - centerY * zoom
        };
      }

      if (shouldAnimate) {
        const duration = step ? step.animationSpeed : 1000;
        let markSettled: () => void = () => undefined;
        cameraSettled = new Promise<void>((resolve) => {
          let settled = false;
          markSettled = () => {
            if (settled) return;
            settled = true;
            resolve();
          };
          // Cytoscape drops `complete` when an animation is interrupted, which
          // happens whenever the next scene is picked mid-flight. Without this
          // the awaiting selection would never run and the scene would be left
          // half applied.
          setTimeout(markSettled, duration + 100);
        });
        cy.animate({
          pan: panZoom?.pan,
          zoom: panZoom?.zoom,
          duration,
          easing: step ? step.animationCurve : 'ease-in-out',
          complete() {
            // Save pan/zoom state immediately after scene animation completes
            savePanZoomState();
            markSettled();
            if (end) {
              end();
            }
          }
        });
      } else {
        cy.pan(panZoom?.pan);
        cy.zoom(panZoom?.zoom);
        // Save pan/zoom state immediately after scene pan/zoom change
        savePanZoomState();
        if (view.includeCurrentState) {
          await selectSelected();
        }
      }
    };

    const applyExpandCollapse = () => {
      cy?.nodes().forEach((node) => {
        if (collapsedNodes.includes(node.id())) {
          expandCollapse.collapse(node);
          node.addClass('collapsed');
          node.data('collapsed', true);
          if (node.data('collapsedChildren')) {
            const payload = {...node.data()};
            delete payload.collapsedChildren;
            delete payload.links;
            if (!isInIframe() && !this.packageService.isDefaulter() && (this.mapRoleService.hasCurrentUserPermission('edit') || this.mapRoleService.hasCurrentUserPermission('manage'))) {
              //@ts-ignore
              this.nodeService.updateNode(payload).pipe(take(1)).subscribe();
            }
          }
        } else {
          node.removeClass('collapsed');
          expandCollapse.expand(node);
          node.data('collapsed', false);
          if (node.data('children')) {
            const payload = {...node.data()};
            delete payload.links;
            if (!isInIframe() && !this.packageService.isDefaulter() && (this.mapRoleService.hasCurrentUserPermission('edit') || this.mapRoleService.hasCurrentUserPermission('manage'))) {
              //@ts-ignore
              this.nodeService.updateNode(payload).pipe(take(1)).subscribe();
            }
          }
        }
      });
    };

    // Must land after the selection: attached modes read cy.nodes(':selected').
    const applyLinkState = () => {
      if (view.linkState !== null) {
        this.settingsService.setLinkViewMode(view.linkState);
      }
      // MAP-1017: restore the "other links" treatment only when it was remembered
      // (gated the same as linkState by "remember link display mode").
      if (view.linkOtherLinksMode !== null && view.linkOtherLinksMode !== undefined) {
        this.settingsService.setOtherLinksMode(view.linkOtherLinksMode);
      }
      this.settingsService.setOtherNodesMode(view.otherNodesMode ?? OtherNodesMode.SHOWN);
    };

    const selectSelected = async () => {
      // Scene doesn't remember selection — leave selection, focus, and sidebar
      // contents untouched so viewing the scene is a no-op for the user's
      // current node/link focus.
      if (selectedElementIds === null) {
        return;
      }
      const applySelection = () => {
        this.cyService.resetAllSelected();
        if (selectedElementIds.length) {
          cy?.elements().deselect();
          const elementsToSelect = cy?.elements()
            .filter((element) => {
              if (selectedElementIds.includes(element.id())) {
                element.data('savedViewSelected', true);
                return true;
              }
              return false;
            });
          this.reportUnrestoredSelection(view, selectedElementIds, elementsToSelect);
          elementsToSelect.select();
          if (view.linkState === LinkViewMode.RELATED) {
            setTimeout(() => {
              elementsToSelect.forEach((element: any) => {
                if (element.isNode()) {
                  clearOtherClasses(element.connectedEdges());
                } else if (element.isEdge()) {
                  clearOtherClasses(element.connectedNodes());
                }
              });
            }, 300)

          }
          DashboardComponent.multiSelectedElements = elementsToSelect.map((element) => element.data());
          if (view.metadata?.focusedElementId) {
            const focusedElement: any = cy?.getElementById(view.metadata.focusedElementId);
            if (!focusedElement || !focusedElement.length) {
              return;
            }
            const isNode = focusedElement.isNode();
            if (isNode) {
              DashboardComponent.tappedNode = focusedElement.data();
            } else {
              DashboardComponent.tappedLink = focusedElement.data();
            }
            const dashRef = (window as any).__dashboardComponentRef;
            if (dashRef?.refreshTappedElements) {
              dashRef.refreshTappedElements();
            }
          }
        } else {
          this.cyService.resetAllSelected();
          cy?.nodes().deselect();
          return;
        }
      };
      await cameraSettled;
      applySelection();
      applyLinkState();

    };

    if (!view.includeCurrentState) {
      await moveViewIntoScene();
      await selectSelected();
    }
    applyExpandCollapse();

    this.viewHasLoaded.pipe(take(1)).subscribe((value) => {
      if (value) {
        this.viewChangeInProgress = false;
      }
    });

    if (view.includeCurrentState) {
      if (view.savedStateId) {
        if (currentSelectedState?.id !== view.savedStateId) {
          this.mapStateService.findOne(view.savedStateId as string)
            .pipe(take(1)) // Auto-unsubscribe after first emission
            .subscribe(async (state) => {
              this.mapStateService.setSceneChangeProgress(true);
              this.mapStateService.selectState(state);
              //TODO: Rework with signals
              setTimeout(async () => {
                await moveViewIntoScene(true);
                this.mapStateService.setSceneChangeProgress(false);
                // Ensure selection happens after scene animation
                setTimeout(async () => {
                  await selectSelected();
                }, 100);
              }, timeout)
            });
        } else {
          this.mapStateService.selectState(currentSelectedState);
          setTimeout(async () => {
            await moveViewIntoScene(true);
            // Ensure selection happens after scene animation
            setTimeout(async () => {
              await selectSelected();
            }, 100);
          }, timeout);
        }
      } else {
        // Working with base state view
        if (currentSelectedState?.id !== undefined && view.savedStateId !== undefined) {
          this.mapStateService.selectState(null);
          setTimeout(async () => {
            await moveViewIntoScene(true);
            // Ensure selection happens after scene animation
            setTimeout(async () => {
              await selectSelected();
            }, 100);
          }, timeout);
        } else {
          await moveViewIntoScene(true);
          await selectSelected();
        }

      }
    }

    // No selection to wait for; otherwise selectSelected applies it.
    if (selectedElementIds === null) {
      applyLinkState();
    }

    // Speak AI description if available
    this.speakAIDescriptionIfAvailable(view, step);
  }

  /** A scene that silently restores fewer elements than it stored. */
  private reportUnrestoredSelection(view: SavedView, wantedIds: string[], found: any): void {
    const foundIds: string[] = found ? found.map((e: any) => e.id()) : [];
    if (foundIds.length === wantedIds.length) return;

    const cy = this.cyService.cy;
    const missing = wantedIds.filter((id) => !foundIds.includes(id));
    const absent = missing.filter((id) => !cy || cy.getElementById(id).empty());
    console.warn(
      `Scene "${view.name}" restored ${foundIds.length}/${wantedIds.length} of its stored elements.`,
      { missing, absentFromGraph: absent, presentButUnmatched: missing.filter((id) => !absent.includes(id)) }
    );
  }

  /**
   * Speak the AI-generated description if available
   */
  private speakAIDescriptionIfAvailable(view: SavedView, step?: TourStep): void {
    // Check if TTS is supported
    if (!this.ttsService.isSupported()) {
      return;
    }

    // Get the AI description from the view
    const aiDescription = (view as any).aiGeneratedDescription;

    if (aiDescription && aiDescription.trim()) {
      try {
        // Add a small delay to let the view animation settle
        setTimeout(() => {
          this.ttsService.speakAIDescription(aiDescription).catch(error => {
            console.error('Failed to speak AI description:', error);
          });
        }, 1500); // 1.5 second delay to allow view transition to complete
      } catch (error) {
        console.error('Error initiating AI description speech:', error);
      }
    }
  }

  /**
   * Show or hide extent debug overlay using canvas
   * Toggle with localStorage.setItem('showExtentDebug', 'true/false')
   */
  private showExtentOverlay(extent?: { x1: number; y1: number; x2: number; y2: number }) {
    const cy = this.cyService.cy;
    if (!cy || !extent) return;

    // Check localStorage flag
    const showDebug = localStorage.getItem('showExtentDebug') === 'true';
    if (!showDebug) return;

    // Remove existing overlay if any
    const existingOverlay = document.getElementById('extent-debug-overlay');
    if (existingOverlay) existingOverlay.remove();

    // Create overlay container
    const overlay = document.createElement('div');
    overlay.id = 'extent-debug-overlay';
    overlay.style.position = 'absolute';
    overlay.style.pointerEvents = 'none';
    overlay.style.zIndex = '9999';
    overlay.style.border = '2px solid red';
    overlay.style.boxSizing = 'border-box';

    // Add to cytoscape container
    const container = cy.container();
    if (!container) return;
    container.appendChild(overlay);

    // Function to update overlay position
    const updatePosition = () => {
      // Get zoom and pan
      const zoom = cy.zoom();
      const pan = cy.pan();

      // Calculate corner positions in screen coordinates
      const x1 = pan.x + extent.x1 * zoom;
      const y1 = pan.y + extent.y1 * zoom;
      const x2 = pan.x + extent.x2 * zoom;
      const y2 = pan.y + extent.y2 * zoom;

      // Position overlay
      overlay.style.left = x1 + 'px';
      overlay.style.top = y1 + 'px';
      overlay.style.width = (x2 - x1) + 'px';
      overlay.style.height = (y2 - y1) + 'px';
    };

    // Update on pan/zoom
    cy.on('pan zoom', updatePosition);
    updatePosition();

    // Remove after 10 seconds
    setTimeout(() => {
      overlay.remove();
      cy.off('pan zoom', updatePosition);
    }, 10000);
  }

  getPanZoomFromExtent(
    extent: { x1: number; y1: number; x2: number; y2: number } | undefined,
    targetSize?: { width?: number; height?: number }
  ): { pan: { x: number; y: number }; zoom: number } | undefined {
    const cy = this.cyService.cy;
    if (!cy || !extent) return undefined;

    const bboxWidth = extent.x2 - extent.x1;
    const bboxHeight = extent.y2 - extent.y1;

    const containerWidth = targetSize?.width ?? cy.width();
    const containerHeight = targetSize?.height ?? cy.height();

    const widthZoom = containerWidth / bboxWidth;
    const heightZoom = containerHeight / bboxHeight;
    const zoom = Math.min(widthZoom, heightZoom);

    const centerX = (extent.x1 + extent.x2) / 2;
    const centerY = (extent.y1 + extent.y2) / 2;

    const pan = {
      x: containerWidth / 2 - centerX * zoom,
      y: containerHeight / 2 - centerY * zoom
    };

    return { pan, zoom };
  }

}
